import React, { useState } from "react";
import { useAuth } from "../lib/auth";
import { motion, AnimatePresence } from "framer-motion";
import {
  House, Lightning, Briefcase, Trophy, Medal, Target,
  UsersThree, Buildings, ClipboardText, SignOut, List as MenuIcon,
  ChartBar, Clock,
} from "@phosphor-icons/react";
import Overview from "./tabs/Overview";
import LogActivity from "./tabs/LogActivity";
import MyAppointments from "./tabs/MyAppointments";
import Cases from "./tabs/Cases";
import Leaderboard from "./tabs/Leaderboard";
import Badges from "./tabs/Badges";
import Challenges from "./tabs/Challenges";
import ActivityReports from "./tabs/ActivityReports";
import ActivityHistory from "./tabs/ActivityHistory";
import AdminAgents from "./tabs/AdminAgents";
import AdminCarriers from "./tabs/AdminCarriers";
import AdminAppointments from "./tabs/AdminAppointments";
import { Button } from "../components/ui/button";

const NAV_AGENT = [
  { key: "overview", label: "Overview", icon: House },
  { key: "log", label: "Log Activity", icon: Lightning, accent: "#2563eb" },
  { key: "cases", label: "Cases", icon: Briefcase },
  { key: "history", label: "Activity History", icon: Clock },
  { key: "reports", label: "Activity Reports", icon: ChartBar },
  { key: "appointments", label: "My Appointments", icon: Briefcase },
  { key: "leaderboard", label: "Leaderboard", icon: Trophy },
  { key: "badges", label: "Leader's Conf. & Badges", icon: Medal },
  { key: "challenges", label: "Challenges", icon: Target },
];

const NAV_ADMIN = [
  { key: "admin_agents", label: "Agents", icon: UsersThree },
  { key: "admin_carriers", label: "Carriers", icon: Buildings },
  { key: "admin_appts", label: "Appointments & Comp", icon: ClipboardText },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const isMP = user?.role === "managing_partner";

  const renderTab = () => {
    switch (tab) {
      case "overview": return <Overview />;
      case "log": return <LogActivity />;
      case "cases": return <Cases />;
      case "reports": return <ActivityReports />;
      case "history": return <ActivityHistory />;
      case "appointments": return <MyAppointments />;
      case "leaderboard": return <Leaderboard />;
      case "badges": return <Badges />;
      case "challenges": return <Challenges />;
      case "admin_agents": return <AdminAgents />;
      case "admin_carriers": return <AdminCarriers />;
      case "admin_appts": return <AdminAppointments />;
      default: return <Overview />;
    }
  };

  const NavButton = ({ item }) => {
    const Icon = item.icon;
    const active = tab === item.key;
    return (
      <button
        onClick={() => { setTab(item.key); setMobileOpen(false); }}
        data-testid={`nav-${item.key}`}
        className={`w-full flex items-center gap-3 px-4 py-3 border-2 border-black font-bold text-sm uppercase tracking-wide transition-all ${
          active
            ? "bg-[#09090b] text-white shadow-[4px_4px_0_0_#2563eb]"
            : "bg-white text-black hover:shadow-[4px_4px_0_0_#09090b] hover:-translate-x-0.5 hover:-translate-y-0.5"
        }`}
        style={active && item.accent ? { boxShadow: `4px 4px 0 0 ${item.accent}` } : undefined}
      >
        <Icon size={18} weight="bold" />
        <span>{item.label}</span>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#f4f4f5]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white border-b-2 border-black">
        <div className="flex items-center justify-between px-4 md:px-8 py-3">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden brut-sm p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
            >
              <MenuIcon size={20} weight="bold" />
            </button>
            <div className="w-7 h-7 bg-[#2563eb] border-2 border-black" />
            <div className="font-display font-black text-lg tracking-tight">CFN</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="font-bold text-sm" data-testid="current-user-name">{user.name}</span>
              <span className="text-xs text-zinc-500 uppercase tracking-widest">{user.role}</span>
            </div>
            <div className="w-9 h-9 border-2 border-black bg-[#2563eb] flex items-center justify-center font-black text-sm">
              {user.name?.[0]?.toUpperCase()}
            </div>
            <Button
              onClick={logout}
              className="rounded-none bg-white text-black border-2 border-black hover:bg-[#fb7185] hover:text-white shadow-[3px_3px_0_0_#09090b] active:shadow-none active:translate-x-1 active:translate-y-1 font-bold uppercase tracking-widest text-xs h-9"
              data-testid="logout-btn"
            >
              <SignOut size={14} weight="bold" className="mr-1" /> Out
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${mobileOpen ? "block" : "hidden"} lg:block fixed lg:sticky top-[65px] z-30 lg:top-[65px] left-0 w-64 lg:w-72 h-[calc(100vh-65px)] overflow-y-auto bg-[#f4f4f5] border-r-2 border-black p-4 space-y-4`}>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2 px-1">My Desk</div>
            <div className="space-y-2">
              {NAV_AGENT.map((item) => <NavButton key={item.key} item={item} />)}
            </div>
          </div>

          {isAdmin && (
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2 px-1 flex items-center gap-2">
                <span>Admin</span>
                <span className="inline-block px-1.5 py-0.5 bg-[#fb7185] text-white text-[10px] border-2 border-black">ADMIN</span>
              </div>
              <div className="space-y-2">
                {NAV_ADMIN.map((item) => <NavButton key={item.key} item={item} />)}
              </div>
            </div>
          )}

          {isMP && (
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2 px-1 flex items-center gap-2">
                <span>My Team</span>
                <span className="inline-block px-1.5 py-0.5 bg-[#2563eb] text-white text-[10px] border-2 border-black">MP</span>
              </div>
              <div className="space-y-2">
                <NavButton item={{ key: "admin_agents", label: "My Agents", icon: UsersThree }} />
                <NavButton item={{ key: "admin_appts", label: "Team Appointments", icon: ClipboardText }} />
              </div>
            </div>
          )}

          <a
            href="/race"
            target="_blank"
            rel="noopener noreferrer"
            className="block brut p-3 mt-4 bg-white text-black hover:-translate-y-0.5 hover:-translate-x-0.5 transition-transform"
            data-testid="race-link"
          >
            <div className="font-display font-black text-sm">LEADER'S CONFERENCE →</div>
            <div className="text-xs text-zinc-500 mt-1">Live public leaderboard for the {new Date().getFullYear()} CFN trip qualifier.</div>
          </a>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-h-[calc(100vh-65px)] p-4 md:p-8 max-w-[1500px] mx-auto w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
            >
              {renderTab()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
